import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CallButton from "@/components/CallButton";
import SEO from "@/components/SEO";
import ServiceStructuredData from "@/components/ServiceStructuredData";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEOSubHero from "@/components/SEOSubHero";
import { Link } from "react-router-dom";
import { Phone, Clock, MapPin, Shield, Wrench, CheckCircle, DoorOpen, KeyRound, Lock, AlertCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/heroes/depannage-urgence-hero.jpg";
import urgence1 from "@/assets/gallery/urgence-1.jpg";
import urgence2 from "@/assets/gallery/urgence-2.jpg";
import urgence3 from "@/assets/gallery/urgence-3.jpg";
import urgence4 from "@/assets/gallery/urgence-4.jpg";
import equipment1 from "@/assets/gallery/urgence-equipment-1.jpg";
import equipment2 from "@/assets/gallery/urgence-equipment-2.jpg";

const DepannageUrgence = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Dépannage Serrurier Urgence 24h/7j Villeurbanne | Tolstoï Serrurier"
        description="Service de dépannage serrurier d'urgence à Villeurbanne et Lyon. Intervention rapide 24h/24 et 7j/7 pour ouverture de porte, changement de serrure, réparation express."
        keywords="dépannage urgence serrurier Villeurbanne, serrurier 24h/24 Villeurbanne, intervention urgence serrure Lyon, serrurier nuit dimanche, dépannage express Villeurbanne"
        canonical="https://www.tolstoi-serrurier.fr/depannage-urgence"
        ogUrl="https://www.tolstoi-serrurier.fr/depannage-urgence"
        ogImage="https://www.tolstoi-serrurier.fr/logo.jpg"
        ogType="business.business"
      />
      <ServiceStructuredData 
        serviceName="Dépannage serrurier urgence 24h/7j"
        serviceDescription="Service d'urgence serrurier disponible 24h/24 à Villeurbanne et Lyon. Intervention rapide en moins de 30 minutes."
        serviceUrl="https://www.tolstoi-serrurier.fr/depannage-urgence"
      />
      <Header />
      
      <div className="container mx-auto px-4">
        <Breadcrumbs items={[
          { label: "Accueil", href: "/" },
          { label: "Dépannage urgence", href: "/depannage-urgence" }
        ]} />
      </div>
      
      <main className="pt-20">
        {/* Hero Section with Background Image */}
        <section className="relative bg-destructive text-destructive-foreground py-20 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src={heroImage} 
              alt="Serrurier urgence Villeurbanne - Van équipé intervention 24h/24"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-destructive/20 to-destructive/10" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Dépannage Serrurier d'Urgence 24h/7j
              </h1>
              <p className="text-xl mb-8 text-destructive-foreground/90">
                Intervention rapide à Villeurbanne et Lyon pour tous vos problèmes de serrurerie. 
                Disponible jour et nuit, week-ends et jours fériés.
              </p>
              <Button size="lg" variant="secondary" className="gap-2" asChild>
                <a href="tel:0482531608">
                  <Phone className="h-5 w-5" />
                  Urgence : 04 82 53 16 08
                </a>
              </Button>
            </div>
          </div>
        </section>

        <SEOSubHero title="Dépannage serrurier en urgence — 24h/7j à Villeurbanne et Lyon">
          <p>
            Intervention <strong>immédiate</strong> en cas de <strong>porte claquée</strong>, <strong>clé cassée</strong>, <strong>serrure bloquée</strong>, ou <strong>effraction</strong>. Délais moyens: 20 à 30 minutes sur Villeurbanne et communes limitrophes.
          </p>
          <p>
            Tarifs <strong>transparents</strong>, devis annoncé avant déplacement, facture pour assurances. Selon le besoin, nous effectuons l'<Link to="/ouverture-porte" className="text-primary hover:underline">ouverture de porte</Link>, la <Link to="/reparation-serrure" className="text-primary hover:underline">réparation de serrure</Link> ou un <Link to="/changement-serrure" className="text-primary hover:underline">changement de serrure</Link>.
          </p>
          <p>
            Zones couvertes: Villeurbanne (Charpennes, Gratte-Ciel, Cusset, République…), Lyon 1–9, et métropole. <Link to="/zones-intervention" className="text-primary hover:underline">Voir les zones</Link>.
          </p>
        </SEOSubHero>

        {/* Situations d'Urgence avec Photos */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4">
                Situations d'Urgence
              </h2>
              <p className="text-center text-muted-foreground mb-12">
                Nous intervenons rapidement pour toutes vos urgences de serrurerie
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={urgence1}
                      alt="Serrurier urgence intervention nuit porte fermée Villeurbanne"
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className="absolute top-4 left-4 w-12 h-12 bg-white/90 backdrop-blur rounded-lg flex items-center justify-center">
                      <DoorOpen className="h-6 w-6 text-destructive" />
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <CardTitle className="text-white text-lg">Porte Claquée</CardTitle>
                    </div>
                  </div>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">
                      Porte fermée avec les clés à l'intérieur. Ouverture rapide sans dégâts.
                    </p>
                  </CardContent>
                </Card>

                <Card className="overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={urgence2}
                      alt="Clé cassée bloquée dans cylindre serrure urgence Lyon"
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className="absolute top-4 left-4 w-12 h-12 bg-white/90 backdrop-blur rounded-lg flex items-center justify-center">
                      <KeyRound className="h-6 w-6 text-destructive" />
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <CardTitle className="text-white text-lg">Clé Cassée</CardTitle>
                    </div>
                  </div>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">
                      Clé cassée dans la serrure. Extraction et remplacement immédiat.
                    </p>
                  </CardContent>
                </Card>

                <Card className="overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={urgence1}
                      alt="Serrure grippée bloquée nécessitant intervention urgence Villeurbanne"
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className="absolute top-4 left-4 w-12 h-12 bg-white/90 backdrop-blur rounded-lg flex items-center justify-center">
                      <Lock className="h-6 w-6 text-destructive" />
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <CardTitle className="text-white text-lg">Serrure Bloquée</CardTitle>
                    </div>
                  </div>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">
                      Serrure grippée ou bloquée. Déblocage ou remplacement selon besoin.
                    </p>
                  </CardContent>
                </Card>

                <Card className="overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={urgence3}
                      alt="Porte endommagée après tentative effraction sécurisation urgence Lyon"
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className="absolute top-4 left-4 w-12 h-12 bg-white/90 backdrop-blur rounded-lg flex items-center justify-center">
                      <AlertCircle className="h-6 w-6 text-destructive" />
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <CardTitle className="text-white text-lg">Effraction</CardTitle>
                    </div>
                  </div>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">
                      Porte endommagée après effraction. Sécurisation immédiate et réparation.
                    </p>
                  </CardContent>
                </Card>

                <Card className="overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={urgence4}
                      alt="Changement serrure après perte clés intervention urgence Villeurbanne"
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className="absolute top-4 left-4 w-12 h-12 bg-white/90 backdrop-blur rounded-lg flex items-center justify-center">
                      <KeyRound className="h-6 w-6 text-destructive" />
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <CardTitle className="text-white text-lg">Perte de Clés</CardTitle>
                    </div>
                  </div>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">
                      Clés perdues ou volées. Ouverture et changement de serrure pour sécurité.
                    </p>
                  </CardContent>
                </Card>

                <Card className="overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={urgence1}
                      alt="Porte bloquée coincée problème mécanique dépannage urgence Lyon"
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className="absolute top-4 left-4 w-12 h-12 bg-white/90 backdrop-blur rounded-lg flex items-center justify-center">
                      <Lock className="h-6 w-6 text-destructive" />
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <CardTitle className="text-white text-lg">Porte Bloquée</CardTitle>
                    </div>
                  </div>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">
                      Porte coincée à cause d'un problème mécanique. Intervention technique rapide.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Pourquoi Nous Choisir */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
              Pourquoi Choisir Tolstoï Serrurier en Urgence ?
            </h2>
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <Clock className="h-6 w-6 text-primary-foreground" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Intervention Rapide</h3>
                  <p className="text-muted-foreground">
                    Arrivée sur site en moins de 30 minutes sur Villeurbanne et communes proches. Service express garanti.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <Shield className="h-6 w-6 text-primary-foreground" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Disponibilité 24h/7j</h3>
                  <p className="text-muted-foreground">
                    Jour, nuit, week-end, jours fériés : nous sommes toujours disponibles pour vous dépanner en urgence.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-primary-foreground" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Tarifs Transparents</h3>
                  <p className="text-muted-foreground">
                    Devis clair avant intervention. Pas de frais cachés ni de suppléments abusifs même en urgence.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <Wrench className="h-6 w-6 text-primary-foreground" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Technicien Qualifié</h3>
                  <p className="text-muted-foreground">
                    Serrurier professionnel certifié avec matériel adapté pour tous types de serrures et fermetures.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Equipment Section with Photos */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4">
                Équipement Professionnel & Certifié
              </h2>
              <p className="text-center text-muted-foreground mb-12">
                Van entièrement équipé et outils professionnels pour toutes interventions d'urgence
              </p>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="relative rounded-xl overflow-hidden shadow-xl">
                  <img 
                    src={equipment1}
                    alt="Van d'intervention serrurier Villeurbanne équipé outils professionnels 24h/24"
                    className="w-full h-80 object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="relative rounded-xl overflow-hidden shadow-xl">
                  <img 
                    src={equipment2}
                    alt="Outils professionnels serrurier certifié intervention urgence Lyon"
                    className="w-full h-80 object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Zone d'Intervention */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">Zone d'Intervention d'Urgence</h2>
              <p className="text-lg text-muted-foreground mb-8">
                Service de dépannage urgent disponible sur Villeurbanne (tous quartiers : Charpennes, République, 
                Gratte-Ciel, Cusset...) et toute la métropole de Lyon : Lyon 1-9, Bron, Vaulx-en-Velin, Caluire, 
                Meyzieu, et communes environnantes.
              </p>
              <Button size="lg" variant="outline" asChild>
                <a href="/zones-intervention">Voir toutes les zones couvertes</a>
              </Button>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Besoin d'un Dépannage Urgent ?</h2>
            <p className="text-xl mb-8 opacity-90">
              Appelez-nous maintenant, nous intervenons en moins de 30 minutes
            </p>
            <Button size="lg" variant="secondary" className="gap-2" asChild>
              <a href="tel:0482531608">
                <Phone className="h-5 w-5" />
                04 82 53 16 08
              </a>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
      <CallButton />
    </div>
  );
};

export default DepannageUrgence;